import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';

import { CommandBarTypedItem } from '#components/command-bar/components/command-bar-typed-item.js';
import { useTRPC } from '#src/trpc.js';
import { organizationLabel, useCommandBarOrganization } from './use-command-bar-organization.js';

import type { CadstromUidResultProps } from '../cadstrom-uid-types.js';

/**
 * Renders a command-bar result for a project id (`p_...`) that navigates to the
 * project's overview page.
 *
 * @param uid - The project id to resolve and display.
 * @param onClose - Dismisses the command bar once navigation begins.
 */
export function ProjectUidResult({ uid, onClose }: CadstromUidResultProps) {
	const navigate = useNavigate();
	const trpc = useTRPC();

	const { data } = useQuery(trpc.project.getPreview.queryOptions({ id: uid }, { throwOnError: false }));
	const preview = data?.projectPreview;
	const organization = useCommandBarOrganization(preview?.ownerId ?? undefined);

	if (!preview || !organization) {
		return null;
	}

	return (
		<Command.Group heading='Projects'>
			<Command.Item
				value={uid}
				keywords={[preview.name, organization.organizationName, 'project']}
				onSelect={() => {
					void navigate({
						to: '/orgs/$orgName/projects/$projectId/overview',
						params: { orgName: organization.organizationName, projectId: preview.id },
					});
					onClose();
				}}
			>
				<CommandBarTypedItem type='project' name={preview.name}>
					<span className='truncate font-normal text-gray-a11'>{organizationLabel(organization)}</span>
				</CommandBarTypedItem>
			</Command.Item>
		</Command.Group>
	);
}
